import React, { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle } from "lucide-react";
import { Message } from "../../types/conversations";
import { MessagesList } from "./MessagesList";
import { MessageInput } from "./MessageInput";
import { BackButton } from "./ChatHeader/BackButton";
import supportService, {
  SupportMessage,
  SupportRoom,
} from "../../services/support";
import conversationsService from "../../services/conversations";
import { toast } from "react-toastify";

interface SupportChatViewProps {
  onBack: () => void;
}

const SUPPORT_CHAT_ID = -1;

function convertToMessage(msg: SupportMessage): Message {
  return {
    id: msg.id,
    chatId: SUPPORT_CHAT_ID,
    senderId: msg.sender_id,
    content: msg.content,
    type: msg.type,
    fileUrl: msg.file_url,
    fileName: msg.file_name,
    timestamp: new Date(msg.timestamp),
    status: msg.status,
  };
}

export const SupportChatView: React.FC<{ onBack: () => void }> = ({
  onBack,
}: SupportChatViewProps) => {
  const [inputValue, setInputValue] = useState("");
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [rooms, setRooms] = useState<SupportRoom[]>([]);
  const [selectedRoomUserId, setSelectedRoomUserId] = useState<number | null>(null);
  const [isSupportUser, setIsSupportUser] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const userId = conversationsService.getCurrentUserId();
    setIsSupportUser(userId === 1);
  }, []);

  const loadMessages = useCallback(async () => {
    setIsLoading(true);
    try {
      const roomUserId =
        isSupportUser && selectedRoomUserId ? selectedRoomUserId : undefined;
      const response = await supportService.listSupportMessages(roomUserId);
      setMessages(response.messages.map(convertToMessage));
    } catch {
      toast.error("Erro ao carregar mensagens");
    } finally {
      setIsLoading(false);
    }
  }, [isSupportUser, selectedRoomUserId]);

  const loadRooms = useCallback(async () => {
    try {
      const response = await supportService.listSupportRooms();
      setRooms(response.rooms);
      if (response.rooms.length > 0 && !selectedRoomUserId) {
        setSelectedRoomUserId(response.rooms[0].user_id);
      }
    } catch {
      toast.error("Erro ao carregar salas");
    }
  }, [selectedRoomUserId]);

  // Carrega salas se for suporte
  useEffect(() => {
    if (isSupportUser) {
      loadRooms();
    }
  }, [isSupportUser, loadRooms]);

  // Carrega mensagens na montagem e ao trocar de sala
  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  // Polling a cada 30s
  useEffect(() => {
    const id = setInterval(loadMessages, 30_000);
    return () => clearInterval(id);
  }, [loadMessages]);

  // Scroll para o final quando chegam novas mensagens
  useEffect(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      });
    });
  }, [messages]);

  const handleSend = async () => {
    if (inputValue.trim() === "" && attachedFiles.length === 0) return;
    if (isSupportUser && !selectedRoomUserId) {
      toast.error("Selecione uma sala primeiro");
      return;
    }

    try {
      const isImage =
        attachedFiles.length > 0 && attachedFiles[0].type.startsWith("image/");

      let fileUrl: string | undefined;
      let fileName: string | undefined;

      if (isImage) {
        toast.info("Enviando imagem...");
        const uploadResult = await supportService.uploadSupportFile(attachedFiles[0]);
        if (!uploadResult) {
          toast.error("Erro ao fazer upload da imagem");
          return;
        }
        fileUrl = uploadResult.url;
        fileName = uploadResult.fileName;
      }

      const messageData: {
        content: string;
        type: "text" | "image";
        file_url?: string;
        file_name?: string;
        recipient_id?: number;
      } = {
        content: inputValue.trim() || "",
        type: isImage ? "image" : "text",
        ...(isImage && { file_url: fileUrl, file_name: fileName }),
        ...(isSupportUser && selectedRoomUserId && { recipient_id: selectedRoomUserId }),
      };

      const response = await supportService.sendSupportMessage(messageData);
      setMessages((prev) => [...prev, convertToMessage(response.message)]);
      setInputValue("");
      setAttachedFiles([]);

      if (isSupportUser) {
        loadRooms();
      }
    } catch {
      toast.error("Erro ao enviar mensagem");
    }
  };

  const getSelectedRoomName = () => {
    if (!selectedRoomUserId || !rooms.length) return "Suporte Easyfit";
    const room = rooms.find((r) => r.user_id === selectedRoomUserId);
    return room ? room.user_name : "Suporte Easyfit";
  };

  return (
    <div className="flex-1 min-h-0 min-w-0 bg-gray-50 dark:bg-gray-900 overflow-hidden flex flex-col relative">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 px-3 py-3 flex items-center gap-3 border-b border-gray-200 dark:border-gray-700 shadow-sm md:px-6 md:py-4 md:gap-4 flex-shrink-0 z-10">
        <BackButton onBack={onBack} />

        {/* Avatar */}
        <div className="relative flex-shrink-0">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center md:w-11 md:h-11">
            <MessageCircle className="w-5 h-5 text-white md:w-6 md:h-6" />
          </div>
          <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-white dark:border-gray-800" />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm text-gray-900 dark:text-white truncate md:text-base">
            {getSelectedRoomName()}
          </h3>
          <p className="text-xs text-green-500 font-medium">Online</p>
        </div>
      </div>

      {/* Seletor de sala (apenas suporte) */}
      {isSupportUser && rooms.length > 0 && (
        <div className="bg-purple-50 dark:bg-gray-700 px-3 py-2 border-b border-gray-200 dark:border-gray-600 flex-shrink-0">
          <label
            htmlFor="support-room-select"
            className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Sala ativa:
          </label>
          <select
            id="support-room-select"
            value={selectedRoomUserId ?? ""}
            onChange={(e) => setSelectedRoomUserId(Number.parseInt(e.target.value, 10))}
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-800 dark:text-white"
          >
            {rooms.map((room) => (
              <option key={room.user_id} value={room.user_id}>
                {room.user_name}
                {room.unread_count > 0 ? ` (${room.unread_count})` : ""}
              </option>
            ))}
          </select>
        </div>
      )}
      {isSupportUser && rooms.length === 0 && !isLoading && (
        <div className="bg-purple-50 dark:bg-gray-700 px-3 py-2 border-b border-gray-200 dark:border-gray-600 flex-shrink-0">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            Nenhuma conversa disponível ainda
          </p>
        </div>
      )}

      {/* Área de mensagens */}
      <div className="conversations-messages-inner flex-1 min-h-0 flex flex-col overflow-hidden">
        {isLoading && (
          <div className="conversations-scroll flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className={`flex ${i % 2 === 0 ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[75%] rounded-lg p-3 space-y-2 ${
                    i % 2 === 0 ? "bg-purple-100 dark:bg-purple-900/30" : "bg-white dark:bg-gray-800"
                  }`}
                >
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-32" />
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse w-24" />
                </div>
              </div>
            ))}
          </div>
        )}
        {!isLoading && messages.length === 0 && (
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
            <div className="w-20 h-20 mb-4 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <MessageCircle className="w-10 h-10 text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
              {isSupportUser && !selectedRoomUserId
                ? "Nenhuma sala selecionada"
                : "Nenhuma mensagem ainda"}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 max-w-xs">
              {isSupportUser && !selectedRoomUserId
                ? "Selecione uma sala para começar a conversar"
                : "Envie uma mensagem para começar a conversa com nosso suporte"}
            </p>
          </div>
        )}
        {!isLoading && messages.length > 0 && (
          <MessagesList
            messages={messages}
            isLoadingMore={false}
            onScroll={() => {}}
            messagesContainerRef={messagesContainerRef}
            messagesEndRef={messagesEndRef}
          />
        )}

        <div className="flex-shrink-0">
          <MessageInput
            value={inputValue}
            attachedFiles={attachedFiles}
            onValueChange={setInputValue}
            onSend={handleSend}
            onFileSelect={(files) => setAttachedFiles((prev) => [...prev, ...files])}
            onRemoveAttachment={(i) =>
              setAttachedFiles((prev) => prev.filter((_, idx) => idx !== i))
            }
          />
        </div>
      </div>
    </div>
  );
};
